import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { MALE_PATTERN_EXTRA, FEMALE_THINNING_EXTRA } from "@/data/hair";
import { HairBookButton, HairCTA, scrollToAnchor } from "@/components/HairShared";
import { setHairStage } from "@/lib/hairStage";
import { trackEvent } from "@/lib/analytics";

const STAGES = MALE_PATTERN_EXTRA.norwood.stages;
const GRADES = FEMALE_THINNING_EXTRA.ludwig.stages;

const SCALES = {
  men: { label: "Men", scale: "Norwood", stages: STAGES, to: "/hair/hair-loss/male-pattern", cols: "grid-cols-2 sm:grid-cols-4 lg:grid-cols-7" },
  women: { label: "Women", scale: "Ludwig", stages: GRADES, to: "/hair/hair-loss/female-thinning", cols: "grid-cols-3 lg:grid-cols-7" },
};

const QuizResult = ({ scale, stage }) => (
  <motion.div
    key={`${scale.scale}-${stage.stage}`}
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -8 }}
    transition={{ duration: 0.4 }}
    className="bg-hair-panel border border-gold/30 p-8 sm:p-10 grid grid-cols-1 lg:grid-cols-11 gap-10"
    id="stage-quiz-result"
    data-testid="stage-quiz-result"
    aria-live="polite"
  >
    <div className="lg:col-span-6">
      <p className="eyebrow eyebrow-dark">Where you might be</p>
      <h3 className="text-warm-white mt-3 text-2xl sm:text-3xl" data-testid="stage-quiz-result-heading">
        <span className="font-serif text-gold mr-3">{scale.scale} {stage.stage}</span>{stage.name}
      </h3>
      <p className="text-warm-white/80 leading-[1.8] mt-5" data-testid="stage-quiz-result-usual">{stage.usual}</p>
      <p className="text-warm-white/50 text-xs leading-relaxed mt-6" data-testid="stage-quiz-disclaimer">
        A drawing can't tell you why your hair is thinning — a scalp examination can. This is a guide to the usual path at this stage, not a diagnosis; the cause, and whether any treatment suits you, are assessed in person.
      </p>
    </div>
    <div className="lg:col-span-5">
      <p className="eyebrow eyebrow-dark">The usual path</p>
      <ol className="mt-4 divide-y divide-gold/15" data-testid="stage-quiz-path">
        {stage.path.map((step, i) => (
          <li key={step.to}>
            <Link to={step.to} data-testid={`stage-quiz-path-${i}`} className="group flex items-center justify-between gap-4 py-3.5 text-warm-white/85 hover:text-gold transition-colors duration-300">
              <span className="flex items-baseline gap-4"><span className="font-serif italic text-gold text-lg leading-none">{i + 1}</span><span className="font-serif text-[17px]">{step.label}</span></span>
              <ArrowRight className="w-4 h-4 shrink-0 opacity-60 group-hover:translate-x-1 transition-transform duration-300" strokeWidth={1.5} />
            </Link>
          </li>
        ))}
      </ol>
      <div className="mt-7 flex flex-wrap items-center gap-5">
        <HairBookButton source={`stage-quiz-${scale.scale.toLowerCase()}-${stage.stage}`} testId="stage-quiz-book" />
        <HairCTA label="Request a callback about this stage" testId="stage-quiz-callback" className="text-[11px] tracking-[0.18em] uppercase font-medium text-gold hover:text-hairline transition-colors duration-300" />
      </div>
      <p className="text-warm-white/50 text-xs mt-4 leading-relaxed" data-testid="stage-quiz-carry-note">
        Your selection ({scale.scale} {stage.stage}) is included with any callback request you send from this site, so the clinic sees it before calling. <Link to={scale.to} data-testid="stage-quiz-stage-link" className="text-gold hover:text-hairline transition-colors duration-300">Each stage explained →</Link>
      </p>
    </div>
  </motion.div>
);

// "Where am I?" — tap the drawing that looks most like you; shows the usual path for that stage.
export const StageQuiz = () => {
  const [scaleKey, setScaleKey] = useState("men");
  const [picked, setPicked] = useState(null);
  const scale = SCALES[scaleKey];
  const selected = picked && scale.stages.find((s) => s.stage === picked);

  const choose = (key) => { setScaleKey(key); setPicked(null); setHairStage(""); };
  const pick = (s) => {
    setPicked(s.stage);
    setHairStage(`${scale.scale} ${s.stage} — ${s.name}`);
    trackEvent("hair_stage_quiz", { scale: scale.scale, stage: s.stage, page: "/hair" });
    if (window.innerWidth < 1024) setTimeout(() => scrollToAnchor("stage-quiz-result"), 120);
  };

  return (
    <div data-testid="stage-quiz">
      <div className="flex flex-wrap items-center gap-3 mb-8" role="tablist" aria-label="Choose a scale">
        {Object.entries(SCALES).map(([key, s]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={scaleKey === key}
            onClick={() => choose(key)}
            data-testid={`stage-quiz-tab-${key}`}
            className={`px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase font-medium border transition-colors duration-300 ${scaleKey === key ? "border-gold bg-gold text-hair-dark" : "border-gold/40 text-warm-white/75 hover:border-gold hover:text-gold"}`}
          >
            {s.label} — the {s.scale} scale
          </button>
        ))}
        <p className="text-warm-white/55 text-sm sm:ml-4" data-testid="stage-quiz-prompt">Tap the drawing that looks most like you.</p>
      </div>
      <ol className={`grid ${scale.cols} gap-x-4 gap-y-6`} data-testid={`stage-quiz-grid-${scaleKey}`}>
        {scale.stages.map((s) => {
          const on = picked === s.stage;
          return (
            <li key={s.stage}>
              <button
                type="button"
                onClick={() => pick(s)}
                aria-pressed={on}
                data-testid={`stage-quiz-option-${scaleKey}-${s.stage}`}
                className={`group relative block w-full text-left p-2 -m-2 rounded-sm transition-colors duration-300 ${on ? "bg-gold/10 ring-1 ring-gold" : "hover:bg-warm-white/[0.04]"}`}
              >
                {on && <span className="absolute top-3 right-3 w-6 h-6 bg-gold text-hair-dark inline-flex items-center justify-center" aria-hidden="true"><Check className="w-3.5 h-3.5" strokeWidth={2} /></span>}
                <NorwoodStageArt stage={s} testId={`stage-quiz-art-${scaleKey}-${s.stage}`} className={`transition-opacity duration-300 ${picked && !on ? "opacity-50 group-hover:opacity-90" : ""}`} />
                <p className="mt-2 text-warm-white/80 text-[13px] leading-snug">
                  <span className="font-serif text-gold text-lg mr-2">{s.stage}</span>
                  {s.name}
                </p>
              </button>
            </li>
          );
        })}
      </ol>
      <div className="mt-10 min-h-[1px]">
        <AnimatePresence mode="wait">
          {selected && <QuizResult scale={scale} stage={selected} />}
        </AnimatePresence>
      </div>
    </div>
  );
};

export const NorwoodStageArt = ({ stage, className = "", testId }) => (
  <img
    src={stage.image}
    alt={stage.alt}
    loading="lazy"
    decoding="async"
    width={960}
    height={560}
    className={`w-full h-auto ${className}`}
    data-testid={testId || `norwood-art-${stage.stage}`}
  />
);

// Compact dark figure for the journal — seven stages in a charcoal panel.
export const NorwoodFigure = ({ caption = MALE_PATTERN_EXTRA.norwood.note }) => (
  <figure className="hair-dark bg-hair-dark my-12 -mx-8 sm:mx-0 px-6 sm:px-8 py-8" data-testid="norwood-figure">
    <p className="eyebrow eyebrow-dark">The Norwood scale</p>
    <ol className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-6 mt-6" data-testid="norwood-figure-grid">
      {STAGES.map((s) => (
        <li key={s.stage} data-testid={`norwood-figure-${s.stage}`}>
          <NorwoodStageArt stage={s} />
          <p className="mt-2 text-warm-white/85 text-[13px] leading-snug">
            <span className="font-serif text-gold text-lg mr-2">{s.stage}</span>
            {s.name}
          </p>
        </li>
      ))}
      <li className="flex items-end">
        <Link to="/hair/hair-loss/male-pattern" data-testid="norwood-figure-link" className="inline-flex items-center gap-2 text-gold hover:text-hairline transition-colors duration-300 text-[11px] tracking-[0.18em] uppercase font-medium pb-1">
          Each stage explained <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
        </Link>
      </li>
    </ol>
    <figcaption className="text-warm-white/45 text-xs mt-6 leading-relaxed">{caption}</figcaption>
  </figure>
);
